import { remoteProdDomain, remoteTestDomain } from '../Config'
import commonRoutes, {
  isValidProjectCode,
  UrlParamKey,
} from '../constants/commonRoutes'
import {
  getPreviewLoginHost,
  getProjectEntryHost,
  isPreviewAuthHost,
  normalizeHostName,
  shouldUsePreviewLoginHost,
  stripMobileSuffix,
} from './authPortalHosts'

const LOGIN_HOST = 'login'
const LOGIN_PATH = '/login'
const PORTAL_PATH = '/portal'
const HTTPS_PROTOCOL = 'https:'
const LOCALHOST = 'localhost'
const LOCALHOST_IP = '127.0.0.1'
// Private LAN IPv4 ranges (RFC1918): 10.0.0.0/8, 172.16.0.0/12,
// 192.168.0.0/16. A phone reaches the dev machine over its LAN IP, so
// these must count as local for device testing.
const PRIVATE_IP_RE =
  /^(10(\.\d{1,3}){3}|192\.168(\.\d{1,3}){2}|172\.(1[6-9]|2\d|3[01])(\.\d{1,3}){2})$/
const LOCAL_LOGIN_BYPASS_KEY = 'local-login'
const EMPTY_STRING = ''

export interface ILoginRedirectTarget {
  pathname?: string
  search?: string
  hash?: string
}

export interface ILoginRedirectContext {
  hostname?: string
  href?: string
}

export interface ILoginRedirectOptions {
  ignoreTokenBypass?: boolean
  /**
   * Build a bare login URL with no `return_to` (STX1-540). Set by the
   * logout-driven redirect: logout ends the session, so it must not
   * stamp a destination — the user lands context-free on the picker.
   * Default (false) stamps the current page as `return_to` for the
   * normal unauthenticated-bounce case.
   */
  omitReturnTo?: boolean
}

function isRemoteTenantDomain(hostname: string): boolean {
  return (
    hostname.endsWith(remoteProdDomain) || hostname.endsWith(remoteTestDomain)
  )
}

export function isLocalHostName(hostname: string): boolean {
  const normalized = normalizeHostName(hostname)
  return (
    normalized === LOCALHOST ||
    normalized === LOCALHOST_IP ||
    PRIVATE_IP_RE.test(normalized)
  )
}

function getConfiguredLoginPort(): string {
  if (typeof CONFIG !== 'undefined' && CONFIG.LOGIN_PORT) {
    return String(CONFIG.LOGIN_PORT)
  }

  return '3004'
}

function isLocalLoginHost(
  hostname: string = location.hostname,
  port: string = location.port,
): boolean {
  return (
    isLocalHostName(hostname) && String(port || '') === getConfiguredLoginPort()
  )
}

function isLoginHostName(hostname: string): boolean {
  if (isLocalHostName(hostname)) {
    return isLocalLoginHost(hostname)
  }

  if (isPreviewAuthHost(hostname)) {
    return true
  }

  return (
    hostname === `${LOGIN_HOST}.${remoteProdDomain}` ||
    hostname === `${LOGIN_HOST}.${remoteTestDomain}`
  )
}

function getDefaultLoginHostName(
  hostname: string,
  search: string = location.search,
): string {
  if (isLocalHostName(hostname)) {
    return `${normalizeHostName(hostname)}:${getConfiguredLoginPort()}`
  }

  if (isPreviewAuthHost(hostname)) {
    return normalizeHostName(hostname)
  }

  // Preview tenants (e.g. pr-123.dev.struxsite.com) default to the
  // SHARED canonical login. The matching preview login
  // (pr-123.login.struxsite.com) exists only when the zoomin PR
  // deployed it (login-affecting changes) — Graph-only / auth-only
  // preview envs have no login VirtualServer there, so an
  // unconditional bounce dead-ends on a 404 (STX1-568). With the
  // STX1-567 return-target resolution, canonical login routes preview
  // visitors back to their preview host, so nothing is lost by
  // defaulting here. `?preview-login-host=1` opts a session into the
  // preview login for end-to-end auth-flow testing; direct visits to a
  // deployed preview login stay there via the isPreviewAuthHost branch
  // above.
  if (shouldUsePreviewLoginHost(search)) {
    const previewLoginHost = getPreviewLoginHost(hostname)
    if (previewLoginHost) {
      return previewLoginHost
    }
    // No preview login derivable from the host (e.g. canonical tenant
    // host with no preview suffix) — fall through to canonical login.
  }

  if (hostname.endsWith(remoteProdDomain)) {
    return `${LOGIN_HOST}.${remoteProdDomain}`
  }

  return `${LOGIN_HOST}.${remoteTestDomain}`
}

function isSafeTenantHostName(hostname: string): boolean {
  return isRemoteTenantDomain(hostname) && !isLoginHostName(hostname)
}

export function isLoginBundle(hostname: string = location.hostname): boolean {
  return isLoginHostName(hostname)
}

export function getLoginHostName(
  hostname: string = location.hostname,
  search: string = location.search,
): string {
  return getDefaultLoginHostName(hostname, search)
}

function getSearchFromHref(href: string): string {
  try {
    return new URL(href).search
  } catch {
    return location.search
  }
}

export function shouldRedirectToLogin(
  hostname: string = location.hostname,
  search: string = location.search,
  options: ILoginRedirectOptions = {},
  port: string = location.port,
): boolean {
  if (isLoginBundle(hostname)) {
    return false
  }

  const searchParams = new URLSearchParams(search)
  if (searchParams.has(LOCAL_LOGIN_BYPASS_KEY)) {
    return false
  }

  if (
    !options.ignoreTokenBypass &&
    (searchParams.has(UrlParamKey.InviteKey) ||
      searchParams.has(UrlParamKey.HandoffCode))
  ) {
    return false
  }

  if (isLocalHostName(hostname)) {
    return !isLocalLoginHost(hostname, port)
  }

  return isRemoteTenantDomain(hostname)
}

function normalizePathname(pathname?: string): string {
  if (!pathname || !pathname.startsWith('/')) {
    return LOGIN_PATH
  }

  return pathname.replace(/^\/{2,}/, '/') || LOGIN_PATH
}

function normalizeSearch(search?: string): string {
  if (!search) {
    return EMPTY_STRING
  }

  return search.startsWith('?') ? search : `?${search}`
}

function normalizeHash(hash?: string): string {
  if (!hash) {
    return EMPTY_STRING
  }

  return hash.startsWith('#') ? hash : `#${hash}`
}

function normalizeReturnPath(pathname: string): string {
  const collapsedSlashes = pathname.replace(/\/{2,}/g, '/')
  return collapsedSlashes.length > 1
    ? collapsedSlashes.replace(/\/+$/, '')
    : collapsedSlashes
}

/**
 * Paths that the tenant bundle no longer serves but can arrive at via
 * stale links, bookmarks, or email templates. On tenant boot we forward
 * the entire URL (with any oobCode/sso_code/token params) to the login
 * portal. `/login` is not in this set — the normal unauth flow handles
 * tenant `/login` visits without needing a pre-init intercept.
 *
 * Signup (`/project/:code/sign-up`) is included: the SignUp component
 * lives only in the login bundle (the legacy tenant-bundle shared
 * SignUp was removed during the Firebase-SDK removal). Without the
 * intercept, the tenant app shell mounts NavBar + JobsiteHomepage with
 * activeProject set from the URL, which dispatches authenticated graph
 * effects against an unauth session. Redirecting to the login portal
 * targets the SignUpView there; the WrappedRoute bypass for
 * isSignUpPageRequested keeps an authenticated-no-project caller off
 * AccessRequester and on the form. Cross-subdomain preview leakage is
 * handled in {@link getDefaultLoginHostName} by routing preview
 * tenants to their matching preview login.
 */
export function isTenantBootInterceptPath(
  pathname: string = location.pathname,
): boolean {
  const normalizedPath = normalizeReturnPath(pathname)

  if (
    normalizedPath === '/save-password' ||
    normalizedPath.startsWith('/sso-callback') ||
    normalizedPath.startsWith('/reset-password')
  ) {
    return true
  }

  return /^\/project\/[^/]+\/sign-up$/.test(normalizedPath)
}

/**
 * Paths we must not send a just-authenticated user back to. `/login`
 * is in this set on top of {@link isTenantBootInterceptPath}:
 * bouncing back to `<tenant>/login` would immediately re-redirect to
 * the portal and loop.
 */
function isUnsafeReturnPath(pathname: string): boolean {
  if (normalizeReturnPath(pathname) === '/login') {
    return true
  }
  return isTenantBootInterceptPath(pathname)
}

export function buildLoginUrl(
  target: ILoginRedirectTarget = {},
  context: ILoginRedirectContext = {},
  options: ILoginRedirectOptions = {},
): string {
  const hostname = context.hostname || location.hostname
  const href = context.href || location.href
  const loginHost = getLoginHostName(hostname, getSearchFromHref(href))
  const protocol = isLocalHostName(hostname)
    ? location.protocol
    : HTTPS_PROTOCOL
  const pathname = normalizePathname(target.pathname)
  const search = normalizeSearch(target.search)
  const hash = normalizeHash(target.hash)
  const url = new URL(`${protocol}//${loginHost}${pathname}${search}${hash}`)

  // return_to is the single navigation-scoped carrier of login context
  // (STX1-540): the destination host+path. The tenant is derived from
  // its hostname at the consumer, so no separate tenant_hint is emitted.
  // Logout omits it (omitReturnTo) — a session-ending bounce carries no
  // destination, so the user lands context-free on the picker.
  if (!options.omitReturnTo) {
    url.searchParams.set(UrlParamKey.ReturnTo, href)
  }

  return url.toString()
}

/**
 * Absolute URL of the User Portal — the target of the tenant app's "My
 * portal" (switch-tenant) action. From a preview tenant host it targets
 * the matching preview login (pr-x.login.struxsite.com) so the whole
 * switch-tenant flow stays inside the preview env; otherwise the canonical
 * login for the environment. No `return_to` — the portal is the
 * destination, not an unauthenticated bounce.
 */
export function buildPortalUrl(
  hostname: string = location.hostname,
  handoffCode?: string,
): string {
  const previewLoginHost = getPreviewLoginHost(hostname)
  const url = new URL(
    previewLoginHost
      ? `${HTTPS_PROTOCOL}//${previewLoginHost}${PORTAL_PATH}`
      : buildLoginUrl({ pathname: PORTAL_PATH }, {}, { omitReturnTo: true }),
  )
  // Carries the tenant session over so the portal opens without a login (STX1-495).
  if (handoffCode) {
    url.searchParams.set(UrlParamKey.HandoffCode, handoffCode)
  }
  return url.toString()
}

/**
 * The `return_to` that lands on a project the portal resolved: its HOME route
 * on the tenant (on a preview login host, the dev tenant's preview desktop).
 * Null when `projectCode` is not a project code.
 */
export function getProjectReturnTo(
  tenantHost: string,
  projectCode: string | undefined,
  hostname: string = location.hostname,
): string | null {
  if (!projectCode || !isValidProjectCode(projectCode)) {
    return null
  }
  const entryHost = getProjectEntryHost(tenantHost, hostname)
  return `${HTTPS_PROTOCOL}//${entryHost}${commonRoutes.HOME.replace(':projectCode', projectCode)}`
}

/**
 * A link that opens a project in a fresh tab: this portal's own `/portal`
 * with the project as `return_to`, so the new tab flies straight over to it
 * (STX1-540). The session is shared through local storage. Null when the
 * project has no usable code.
 */
export function buildProjectEntryUrl(
  tenantHost: string,
  projectCode: string | undefined,
  origin: string = location.origin,
  hostname: string = location.hostname,
): string | null {
  const returnTo = getProjectReturnTo(tenantHost, projectCode, hostname)
  if (!returnTo) {
    return null
  }
  const url = new URL(PORTAL_PATH, origin)
  url.searchParams.set(UrlParamKey.ReturnTo, returnTo)
  return url.toString()
}

/**
 * Full-page navigation to the User Portal, carrying this session over with a
 * handoff code (STX1-495). If the mint fails, the portal opens without one.
 */
export async function openUserPortal(
  mintHandoffCode: () => Promise<string>,
  assign: (url: string) => void = url => location.assign(url),
): Promise<void> {
  let handoffCode: string | undefined
  try {
    handoffCode = await mintHandoffCode()
  } catch (error) {
    console.warn('Handoff code mint failed; opening portal without it', error)
  }
  assign(buildPortalUrl(location.hostname, handoffCode))
}

export function redirectToLogin(
  target: ILoginRedirectTarget = {},
  options: ILoginRedirectOptions = {},
): boolean {
  if (!shouldRedirectToLogin(location.hostname, location.search, options)) {
    return false
  }

  location.assign(buildLoginUrl(target, {}, options))
  return true
}

/**
 * Validate a candidate `return_to` URL: must point at a real tenant
 * domain (not the login bundle) over https. In non-production builds a
 * local host (localhost / private LAN IP) is additionally accepted so
 * mobile device testing against a dev server returns to its origin; that
 * branch is dead-code-eliminated from production bundles.
 */
function isSafeReturnTo(urlString: string): boolean {
  try {
    const url = new URL(urlString)
    if (isSafeTenantHostName(url.hostname) && url.protocol === HTTPS_PROTOCOL) {
      return true
    }
    // Local-dev only: carry an http return_to back to THIS page's own host
    // (the dev box — any port, since desktop/mobile/login run on different
    // ports of one LAN IP). Restricting to location.hostname stops a crafted
    // return_to from sending the appended inviteKey to another host on the
    // dev LAN. Eliminated from production bundles.
    return (
      process.env.NODE_ENV !== 'production' &&
      isLocalHostName(url.hostname) &&
      url.hostname === location.hostname
    )
  } catch {
    return false
  }
}

/**
 * The validated `return_to` destination from the address bar (or a
 * provided search string). This is the single navigation-scoped carrier
 * of login context (STX1-540) — it replaces the former
 * `auth_portal_*` sessionStorage cache. Returns null when absent or
 * unsafe (wrong domain, non-https non-localhost, malformed).
 */
export function getReturnToFromUrl(
  search: string = location.search,
): string | null {
  try {
    const returnTo = new URLSearchParams(search).get(UrlParamKey.ReturnTo)
    return returnTo && isSafeReturnTo(returnTo) ? returnTo : null
  } catch {
    return null
  }
}

/**
 * The tenant host the login bundle should talk to, derived from the
 * `return_to` destination's hostname. Replaces the former
 * `resolveLoginTenantHost` (which read a separate `tenant_hint`): the
 * destination URL already names its host, so the tenant is just
 * `new URL(return_to).hostname`. Returns null when there is no safe
 * destination on the URL.
 */
export function getReturnToTenantHost(
  search: string = location.search,
): string | null {
  const returnTo = getReturnToFromUrl(search)
  if (!returnTo) {
    return null
  }
  try {
    return new URL(returnTo).hostname
  } catch {
    return null
  }
}

export interface ITenantReturnTarget {
  /** Host (including port, for localhost dev) the redirect should target. */
  host: string
  /** Path + search + hash to append to the host. */
  path: string
}

/**
 * Local-dev post-login target: when `return_to` points at a local host
 * (localhost / private LAN IP), return there directly, bypassing tenant
 * resolution. A LAN IP never canonicalizes to a remote tenant, so
 * `getTenantReturnTarget` cannot route a device back to its dev origin;
 * here the picked tenant only selects the backend (reached through the
 * dev server's proxy), while the browser must return to its own origin.
 * Returns null in production builds and for non-local destinations.
 */
export function getLocalReturnTarget(
  returnTo?: string | null,
): ITenantReturnTarget | null {
  // Guard prod first so getReturnToFromUrl() is never evaluated in a
  // production bundle (a default arg would run it before this check).
  if (process.env.NODE_ENV === 'production') {
    return null
  }

  const target = returnTo === undefined ? getReturnToFromUrl() : returnTo
  if (!target) {
    return null
  }

  try {
    const url = new URL(target)
    if (!isLocalHostName(url.hostname) || isUnsafeReturnPath(url.pathname)) {
      return null
    }
    return { host: url.host, path: `${url.pathname}${url.search}${url.hash}` }
  } catch {
    return null
  }
}

/**
 * True when `hostname` belongs to `tenantHost`: the canonical host
 * itself, a `-mobile` twin (acme-mobile.struxhub.com), or a preview-env
 * subordinate (pr-x.dev.struxsite.com / pr-x-mobile.dev.struxsite.com →
 * dev.struxsite.com). Same canonicalization the hostname auto-match in
 * Authentication.store.initAuthUser and the auth service's tenant-host
 * normalization use, so the hosts the login portal will return to match
 * the set the backend resolves to the same tenant.
 */
function isHostOfTenant(hostname: string, tenantHost: string): boolean {
  // URL.hostname is always lowercase; the tenant host (backend registry)
  // is not guaranteed to be — normalize both sides.
  const normalizedTenant = normalizeHostName(tenantHost)
  if (!normalizedTenant) {
    return false
  }
  const matches = (h: string) =>
    h === normalizedTenant || h.endsWith(`.${normalizedTenant}`)
  const normalizedHost = normalizeHostName(hostname)
  // Preview prefixes are caught by the suffix match; the `-mobile` twin
  // lives in the first label, so also try the demobilized form.
  return matches(normalizedHost) || matches(stripMobileSuffix(normalizedHost))
}

/**
 * Resolve the post-login redirect target for the picked tenant from the
 * `return_to` destination (STX1-540 — consumption-side validation).
 *
 * The picker pick (or single-tenant auto-select) selects the *tenant*;
 * `return_to` selects the *host and path within it*. A destination whose
 * hostname canonicalizes to the picked tenant keeps its **full original
 * host** — this is what routes preview-env (pr-x.dev.struxsite.com) and
 * mobile-twin (acme-mobile.…) visitors back where they came from with
 * their deep link. A destination pointing at any other tenant is
 * discarded (null), keeping the STX1-539 cross-tenant guarantees;
 * callers fall back to the canonical tenant host + /projects.
 *
 * `returnTo` defaults to the address-bar value; pass it explicitly when
 * resolving from a non-default search (e.g. the SSO `state` echo).
 */
export function getTenantReturnTarget(
  tenantHost: string,
  returnTo: string | null = getReturnToFromUrl(),
): ITenantReturnTarget | null {
  if (!returnTo || !tenantHost) {
    return null
  }

  try {
    const returnUrl = new URL(returnTo)

    if (!isHostOfTenant(returnUrl.hostname, tenantHost)) {
      return null
    }

    // Never bounce users back into legacy login paths on the tenant host
    if (isUnsafeReturnPath(returnUrl.pathname)) {
      return null
    }

    const path = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`

    // isHostOfTenant validates the port-less hostname, so only the
    // hostname may flow into the redirect target — propagating
    // returnUrl.host verbatim would let a crafted return URL smuggle an
    // unvalidated port onto a trusted host. Localhost is the one place a
    // port is meaningful (vite dev servers).
    const host = isLocalHostName(returnUrl.hostname)
      ? returnUrl.host
      : returnUrl.hostname

    return { host, path }
  } catch {
    return null
  }
}
